import axios from 'axios';
import { getCsrfToken, getCurrentCsrfToken, resetCsrfToken } from './csrfService';

export type Rating = 'like' | 'dislike' | null;


export class RatingService {
  private static baseURL = import.meta.env.VITE_SHARE_SERVER_URL || 'http://localhost:3002';

  /**
   * Сохраняет или изменяет оценку анализа
   * @param externalId - Внешний ID анализа
   * @param rating - Новая оценка ('like' | 'dislike' | null)
   * @returns Promise с результатом
   */
  static async setRating(
    externalId: string,
    rating: Rating
  ): Promise<{ success: boolean; userRating: Rating }> {
    try {
      // Получаем CSRF токен
      let csrfToken = getCurrentCsrfToken();
      if (!csrfToken) {
        console.log('CSRF: Токен не найден, запрашиваем новый');
        csrfToken = await getCsrfToken();
      } else {
        console.log('CSRF: Используем существующий токен из кэша');
      }

      const response = await axios.post(
        `${this.baseURL}/api/analysis/${externalId}/rating`,
        {
          rating: rating
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken
          },
          timeout: 10000,
          withCredentials: true // Важно для CSRF токенов
        }
      );

      return {
        success: response.data.success,
        userRating: response.data.userRating || null
      };
    } catch (error) {
      console.error('Error setting rating:', error);
      
      if (axios.isAxiosError(error)) {
        // Если ошибка CSRF, сбрасываем токен и пробуем еще раз
        if (error.response?.data?.code === 'CSRF_ERROR') {
          resetCsrfToken();
          throw new Error('Ошибка CSRF токена. Попробуйте еще раз.');
        }
        
        if (error.response?.status === 400) {
          throw new Error(error.response.data.error || 'Некорректные данные запроса');
        } else if (error.response?.status === 404) {
          throw new Error('Анализ не найден');
        } else if (error.response?.status === 500) {
          throw new Error('Внутренняя ошибка сервера');
        } else {
          throw new Error(error.response?.data?.error || 'Ошибка при сохранении оценки');
        }
      }
      
      throw new Error('Ошибка сети при сохранении оценки');
    }
  }

  /**
   * Получает сохраненную оценку пользователя из localStorage
   * @param externalId - Внешний ID анализа
   * @returns Сохраненная оценка или null
   */
  static getUserRating(externalId: string): Rating {
    try {
      const key = `analysis_rating_${externalId}`;
      const stored = localStorage.getItem(key);
      if (stored === 'like' || stored === 'dislike') {
        return stored;
      }
      return null;
    } catch (error) {
      console.error('Error getting user rating from localStorage:', error);
      return null;
    }
  }

  /**
   * Сохраняет оценку пользователя в localStorage
   * @param externalId - Внешний ID анализа
   * @param rating - Оценка для сохранения
   */
  static saveUserRating(externalId: string, rating: Rating): void {
    try {
      const key = `analysis_rating_${externalId}`;
      if (rating === null) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, rating);
      }
    } catch (error) {
      console.error('Error saving user rating to localStorage:', error);
    }
  }
}

